import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { authReducer, initialAuthState, AUTH_ACTIONS } from './AuthStore';
import { connectWebSocket, disconnectWebSocket } from 'connectors/websocket';

// Module-level ref so API interceptors can read the token without React hooks
let _authToken = null;
export const getAuthToken = () => _authToken;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  // The websocket handshake is authenticated too, so it can only be opened once
  // a token exists — and must be dropped as soon as the session ends.
  useEffect(() => {
    _authToken = state.token;
    if (state.token) connectWebSocket(state.token);
    else disconnectWebSocket();
    return disconnectWebSocket;
  }, [state.token]);

  // Stable identities: every consumer hook (useApiCall above all) is memoised on
  // these, and a new function each render would invalidate every effect downstream.
  const setToken = useCallback((token) => dispatch({ type: AUTH_ACTIONS.SET_TOKEN, token }), []);
  const setErrorText = useCallback((errorText) => dispatch({ type: AUTH_ACTIONS.SET_ERROR, errorText }), []);
  const logout = useCallback(() => dispatch({ type: AUTH_ACTIONS.LOGOUT }), []);

  const value = useMemo(
    () => ({ ...state, setToken, setErrorText, logout }),
    [state, setToken, setErrorText, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
